// Mobile voice call dock — sits directly below the fixed channel header on small
// screens while on a call. Extracted verbatim from ChatLayout's inline render
// (step 7.6.11). Structural props only — no imports back into ChatLayout
// (refactor rule 5); prop names mirror VoiceDock for consistency.
//
// The parent decides whether to render this at all (the `voiceJoined` gate stays
// at the call site). Navigation stays parent-side via `onJumpToCall` (collapses
// switchVoid + handleSelectChannel), exactly as VoiceDock does.
//
// Static-verify only: LiveKit is not configured in the dev env, so this move is
// proven by tsc + vite build + line-by-line diff audit, not a live call.
type MobileVoiceDockProps = {
  voiceActiveChannelId: string | null;
  voiceActiveChannelName: string | null;
  voiceMuted: boolean;
  videoEnabled: boolean;
  videoBusy: boolean;
  /** Navigation stays parent-side (switchVoid + handleSelectChannel). */
  onJumpToCall: () => void;
  toggleMute: () => void;
  toggleVideo: () => void;
  leaveVoice: () => void;
};

export default function MobileVoiceDock({
  voiceActiveChannelId,
  voiceActiveChannelName,
  voiceMuted,
  videoEnabled,
  videoBusy,
  onJumpToCall,
  toggleMute,
  toggleVideo,
  leaveVoice,
}: MobileVoiceDockProps) {
  return (
    <div
      className="sm:hidden fixed left-0 right-0 flex items-center gap-2 px-4 bg-neutral-950/95 border-b border-emerald-900/60 backdrop-blur-xl shadow-[0_4px_24px_rgba(0,0,0,0.4)]"
      style={{ top: 'calc(env(safe-area-inset-top) + 64px)', paddingTop: 8, paddingBottom: 8, zIndex: 39 }}
    >
      {/* Channel info */}
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
          <span className="text-meta text-emerald-300 truncate">
            {voiceActiveChannelName || voiceActiveChannelId || 'Voice'}
          </span>
        </div>
        <button
          className="text-left text-meta text-fg-2 hover:text-emerald-200 transition-colors truncate"
          onClick={onJumpToCall}
        >
          Return to channel →
        </button>
      </div>

      {/* Mute */}
      <button
        className={`h-10 w-10 rounded-full flex items-center justify-center border transition-colors ${voiceMuted ? 'border-red-500/60 bg-red-500/15 text-red-300' : 'border-white/15 bg-white/[0.06] text-fg-2'}`}
        onClick={toggleMute}
        title={voiceMuted ? 'Unmute' : 'Mute'}
        aria-label={voiceMuted ? 'Unmute' : 'Mute'}
      >
        {voiceMuted ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="m9 9-5 5v-4a4 4 0 0 1 4-4h.18"/><path d="M15 9.34V5a4 4 0 0 0-7.64-1.12"/><path d="m17 16 5 5"/><path d="m17 11 5-5"/><path d="M15 19a4 4 0 0 1-6.8-2.6L9 15"/><path d="M9 9v6"/></svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M12 18a4 4 0 0 0 4-4V5a4 4 0 1 0-8 0v9a4 4 0 0 0 4 4Z"/><path d="M19 10v2a7 7 0 0 1-7 7"/><path d="M5 10v2a7 7 0 0 0 7 7"/></svg>
        )}
      </button>

      {/* Camera */}
      <button
        className={`h-10 w-10 rounded-full flex items-center justify-center border transition-colors ${videoEnabled ? 'border-emerald-500/60 bg-emerald-500/15 text-emerald-300' : 'border-white/15 bg-white/[0.06] text-fg-2'} disabled:opacity-40`}
        onClick={() => toggleVideo()}
        disabled={videoBusy}
        title={videoEnabled ? 'Stop camera' : 'Start camera'}
        aria-label={videoEnabled ? 'Stop camera' : 'Start camera'}
      >
        {videoEnabled ? (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="m17 7 4-4v18l-4-4"/><rect x="3" y="5" width="14" height="14" rx="2" ry="2"/></svg>
        ) : (
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M17 10.5V6a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4.5l4 4v-11l-4 4Z"/><line x1="2" y1="2" x2="22" y2="22"/></svg>
        )}
      </button>

      {/* Disconnect */}
      <button
        className="h-10 w-10 rounded-full flex items-center justify-center border border-red-500/50 bg-red-500/15 text-red-300 transition-colors active:bg-red-500/30"
        onClick={leaveVoice}
        title="Leave call"
        aria-label="Leave call"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4"><path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.42 19.42 0 0 1 4.43 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.34 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.32 9.9"/><line x1="23" y1="1" x2="1" y2="23"/></svg>
      </button>
    </div>
  );
}
